/**
 * Notification Channels API Service
 *
 * Handles all notification channel CRUD and verification operations.
 * Services are stateless and return promises - UI concerns belong in hooks.
 *
 * @see ../ARCHITECTURE.md for design principles
 */

import { authenticatedFetch } from '../../utils/authenticated-fetch';
import {
  NotificationChannelApiError,
  type CreateEmailChannelParams,
  type CreateSlackChannelParams,
  type CreateWebhookChannelParams,
  type UpdateChannelParams,
  type VerifyChannelParams,
  type FetchChannelsResult,
  type NotificationChannel,
} from './types';

/**
 * Fetch all notification channels for the current shop
 *
 * @returns Array of notification channels
 * @throws NotificationChannelApiError if the operation fails
 */
export async function fetchChannels(): Promise<NotificationChannel[]> {
  const response = await authenticatedFetch('/api/notification-channels');

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Failed to fetch notification channels',
      response.status
    );
  }

  const data = (await response.json()) as FetchChannelsResult;
  return data.channels || [];
}

/**
 * Test a notification channel by sending a test message
 *
 * @param channelId - The ID of the channel to test
 * @throws NotificationChannelApiError if the operation fails
 */
export async function testChannel(channelId: number): Promise<void> {
  const response = await authenticatedFetch(`/api/notification-channels/${channelId}/test`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Failed to send test notification',
      response.status,
      channelId
    );
  }
}

/**
 * Toggle a channel's enabled state
 *
 * @param params - Channel ID and new enabled state
 * @throws NotificationChannelApiError if the operation fails
 */
export async function updateChannel(params: UpdateChannelParams): Promise<void> {
  const { channelId, enabled } = params;

  const response = await authenticatedFetch(`/api/notification-channels/${channelId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Failed to update channel',
      response.status,
      channelId
    );
  }
}

/**
 * Delete a notification channel
 *
 * @param channelId - The ID of the channel to delete
 * @throws NotificationChannelApiError if the operation fails
 */
export async function deleteChannel(channelId: number): Promise<void> {
  const response = await authenticatedFetch(`/api/notification-channels/${channelId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Failed to delete channel',
      response.status,
      channelId
    );
  }
}

/**
 * Send verification code to a channel
 *
 * @param channelId - The ID of the channel to verify
 * @throws NotificationChannelApiError if the operation fails
 */
export async function sendVerification(channelId: number): Promise<void> {
  const response = await authenticatedFetch(`/api/notification-channels/${channelId}/send-verification`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Failed to send verification',
      response.status,
      channelId
    );
  }
}

/**
 * Verify a channel with a verification code
 *
 * @param params - Channel ID and verification code
 * @throws NotificationChannelApiError if the operation fails
 */
export async function verifyChannel(params: VerifyChannelParams): Promise<void> {
  const { channelId, code } = params;

  const response = await authenticatedFetch(`/api/notification-channels/${channelId}/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Verification failed',
      response.status,
      channelId
    );
  }
}

/**
 * Create an email notification channel
 *
 * @param params - Channel name and email configuration
 * @throws NotificationChannelApiError if the operation fails
 */
export async function createEmailChannel(params: CreateEmailChannelParams): Promise<void> {
  const { name, email, emailType = 'notification' } = params;

  const response = await authenticatedFetch('/api/notification-channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      channel_type: 'email',
      channel_name: name,
      config: { email_address: email, email_type: emailType },
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Failed to add email channel',
      response.status
    );
  }
}

/**
 * Create a Slack notification channel
 *
 * @param params - Channel name and webhook URL
 * @throws NotificationChannelApiError if the operation fails
 */
export async function createSlackChannel(params: CreateSlackChannelParams): Promise<void> {
  const { name, webhookUrl } = params;

  const response = await authenticatedFetch('/api/notification-channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      channel_type: 'slack',
      channel_name: name,
      config: { webhook_url: webhookUrl },
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Failed to add Slack channel',
      response.status
    );
  }
}

/**
 * Create a webhook notification channel
 *
 * @param params - Channel name, URL, and optional headers
 * @throws NotificationChannelApiError if the operation fails
 */
export async function createWebhookChannel(params: CreateWebhookChannelParams): Promise<void> {
  const { name, url, headers } = params;

  const config: Record<string, unknown> = { url };
  if (headers && Object.keys(headers).length > 0) {
    config.headers = headers;
  }

  const response = await authenticatedFetch('/api/notification-channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      channel_type: 'webhook',
      channel_name: name,
      config,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new NotificationChannelApiError(
      errorData.error || 'Failed to add webhook channel',
      response.status
    );
  }
}
